import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Country } from 'src/app/models/common/country.model';

export interface CountryPhoneRule {
  countryCode: string;
  phoneCode: string;
  mask: string;
  placeholder: string;
  regex: RegExp;
}

const GCC_PHONE_RULES: Record<string, CountryPhoneRule> = {
  SA: { countryCode: 'SA', phoneCode: '+966', mask: '+966 5X XXX XXXX', placeholder: '+966 5X XXX XXXX', regex: /^\+9665\d{8}$/ },
  AE: { countryCode: 'AE', phoneCode: '+971', mask: '+971 5X XXX XXXX', placeholder: '+971 5X XXX XXXX', regex: /^\+9715\d{8}$/ },
  BH: { countryCode: 'BH', phoneCode: '+973', mask: '+973 3XXX XXXX', placeholder: '+973 3XXX XXXX', regex: /^\+9733\d{7}$/ },
  KW: { countryCode: 'KW', phoneCode: '+965', mask: '+965 [5|6|9]XXX XXXX', placeholder: '+965 5XXX XXXX', regex: /^\+965[569]\d{7}$/ },
  OM: { countryCode: 'OM', phoneCode: '+968', mask: '+968 [7|9]XXX XXXX', placeholder: '+968 9XXX XXXX', regex: /^\+968[79]\d{7}$/ },
  QA: { countryCode: 'QA', phoneCode: '+974', mask: '+974 [3|5|6|7]XXX XXXX', placeholder: '+974 3XXX XXXX', regex: /^\+974[3567]\d{7}$/ }
};

export function phoneMaskForCountry(country?: Country | null): string {
  return phoneRuleForCountry(country)?.mask ?? `${country?.phoneCode || '+'} phone number`;
}

export function phonePlaceholderForCountry(country?: Country | null): string {
  return phoneRuleForCountry(country)?.placeholder ?? `${country?.phoneCode || '+'} phone number`;
}

export function normalizePhoneForCountry(value?: string | null, country?: Country | null): string | null {
  const raw = `${value || ''}`.trim();
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (raw.startsWith('+')) return `+${digits}`;
  if (raw.startsWith('00') && digits.length > 2) return `+${digits.slice(2)}`;

  const countryDigits = `${country?.phoneCode || ''}`.replace(/\D/g, '');
  if (!countryDigits) return `+${digits}`;
  if (digits.startsWith(countryDigits)) return `+${digits}`;
  return `+${countryDigits}${digits.replace(/^0+/, '')}`;
}

export function isValidPhoneForCountry(value?: string | null, country?: Country | null): boolean {
  const normalized = normalizePhoneForCountry(value, country);
  if (!normalized) return false;

  const configuredRegex = regexFromCountry(country);
  if (configuredRegex) {
    const national = nationalDigits(normalized, country);
    return configuredRegex.test(`${value || ''}`) || configuredRegex.test(normalized) || configuredRegex.test(national);
  }

  const rule = phoneRuleForCountry(country);
  if (rule) return rule.regex.test(normalized);
  return /^\+\d{8,15}$/.test(normalized);
}

export function countryPhoneValidator(countryResolver: () => Country | null | undefined): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = `${control.value || ''}`.trim();
    if (!value) return null;
    return isValidPhoneForCountry(value, countryResolver()) ? null : { invalidPhoneForCountry: true };
  };
}

function phoneRuleForCountry(country?: Country | null): CountryPhoneRule | undefined {
  const code = country?.countryCode?.trim().toUpperCase();
  return code ? GCC_PHONE_RULES[code] : undefined;
}

function regexFromCountry(country?: Country | null): RegExp | null {
  const source = country?.phoneRegex?.trim();
  if (!source) return null;
  try {
    return new RegExp(source);
  } catch {
    return null;
  }
}

function nationalDigits(normalizedPhone: string, country?: Country | null): string {
  const digits = normalizedPhone.replace(/\D/g, '');
  const countryDigits = `${country?.phoneCode || ''}`.replace(/\D/g, '');
  return countryDigits && digits.startsWith(countryDigits) ? digits.slice(countryDigits.length) : digits;
}
